import { Alert, Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import {
  getGiftCertificateFiles,
  uploadGiftCertificateFile,
  deleteGiftCertificateFile,
  GiftCertificateFileResponse,
} from 'src/shared/services/fundraiserService';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { FundraiserBackgroundCropperModal } from './fundraiserBackgroundCropperModal';
import { FundraiserMotifCropperModal } from './fundraiserMotifCropperModal';
import { FundraiserSuggestionsModal } from './fundraiserSuggestionsModal';

type FundraiserGiftCertificateTemplatesProps = {
  fundraiserId: number;
};

type FileUploadType = 'background' | 'motif' | 'logo';
type UploadMode = 'background' | 'motif';

const allowedMimeTypes = ['image/png', 'image/jpeg'];

// Placeholder wireframe som visar gåvobladets layout
const PlaceholderWireframe = ({
  mode,
  selected,
  onClick,
  label,
  description,
}: {
  mode: UploadMode;
  selected: boolean;
  onClick: () => void;
  label: string;
  description: string;
}) => {
  const isBackground = mode === 'background';
  let wireframeBg = '#fff';
  if (isBackground) wireframeBg = selected ? 'primary.100' : 'grey.100';

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1,
        backgroundColor: selected ? 'primary.50' : '#fff',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
      }}
    >
      <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, textAlign: 'center' }}>
        {label}
      </Typography>
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: 'block', mb: 1, textAlign: 'center', fontSize: '0.65rem', lineHeight: 1.3 }}
      >
        {description}
      </Typography>
      {/* A4 wireframe */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1.414',
          border: '1px solid',
          borderColor: isBackground && selected ? 'primary.main' : 'grey.300',
          borderRadius: 0.5,
          backgroundColor: wireframeBg,
          overflow: 'hidden',
        }}
      >
        {/* Motivbild-yta i toppen — bara på motiv-placeholdern */}
        {!isBackground && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              width: 'calc(100% - 4px)',
              height: '45%',
              border: '1px dashed',
              borderColor: selected ? 'primary.main' : 'grey.300',
              borderRadius: 0.5,
              backgroundColor: selected ? 'primary.100' : 'grey.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant='caption' color={selected ? 'primary.main' : 'text.disabled'} sx={{ fontSize: '0.6rem' }}>
              Motiv
            </Typography>
          </Box>
        )}

        {/* Logo-plats */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30%',
            height: '8%',
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant='caption' color='text.disabled' sx={{ fontSize: '0.5rem' }}>
            Logo
          </Typography>
        </Box>

        {/* Textplatser — högre upp på bakgrunds-placeholdern */}
        <Box
          sx={{
            position: 'absolute',
            top: isBackground ? '15%' : '60%',
            left: '15%',
            right: '15%',
            display: 'flex',
            flexDirection: 'column',
            gap: '4%',
          }}
        >
          <Box sx={{ height: 4, backgroundColor: 'grey.300', borderRadius: 1 }} />
          <Box sx={{ height: 3, backgroundColor: 'grey.200', borderRadius: 1, mx: '10%' }} />
          <Box sx={{ height: 3, backgroundColor: 'grey.200', borderRadius: 1, mx: '20%' }} />
          <Box sx={{ height: 3, backgroundColor: 'grey.200', borderRadius: 1, mx: '10%' }} />
        </Box>

        {/* Bakgrunds-label */}
        {isBackground && (
          <Typography
            variant='caption'
            color={selected ? 'primary.main' : 'text.disabled'}
            sx={{ position: 'absolute', bottom: '16%', width: '100%', textAlign: 'center', fontSize: '0.6rem' }}
          >
            Bakgrund
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const FundraiserGiftCertificateTemplates = (props: FundraiserGiftCertificateTemplatesProps) => {
  const { fundraiserId } = props;
  const { t } = useTranslation(['fundraiser']);
  const isMounted = useIsMounted();

  const logoInputRef = useRef<HTMLInputElement>(null);

  const [backgrounds, setBackgrounds] = useState<GiftCertificateFileResponse[]>([]);
  const [motifs, setMotifs] = useState<GiftCertificateFileResponse[]>([]);
  const [logo, setLogo] = useState<GiftCertificateFileResponse | null>(null);
  const [maxMotifs, setMaxMotifs] = useState<number>(6);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<string>('');
  const [uploadMode, setUploadMode] = useState<UploadMode>('background');
  const [cropperOpen, setCropperOpen] = useState<boolean>(false);
  const [motifCropperOpen, setMotifCropperOpen] = useState<boolean>(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getGiftCertificateFiles(fundraiserId);
      if (isMounted()) {
        const cacheBuster = `?v=${Date.now()}`;
        const addCacheBuster = (f: GiftCertificateFileResponse) => ({ ...f, url: f.url + cacheBuster });
        setBackgrounds(res.data.backgrounds.map(addCacheBuster));
        setMotifs(res.data.motifs.map(addCacheBuster));
        setLogo(res.data.logo ? addCacheBuster(res.data.logo) : null);
        setMaxMotifs(res.data.maxMotifs);
        // Förvälj typ baserat på vad som redan finns uppladdad
        if (res.data.motifs.length > 0 && res.data.backgrounds.length === 0) {
          setUploadMode('motif');
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [fundraiserId, isMounted]);

  useEffect(() => {
    if (fundraiserId > 0) {
      fetchFiles();
    }
  }, [fetchFiles, fundraiserId]);

  const handleUploadClick = (uploadType: FileUploadType) => {
    if (uploadType === 'background') {
      setCropperOpen(true);
      return;
    }
    if (uploadType === 'motif') {
      setMotifCropperOpen(true);
      return;
    }
    if (uploadType === 'logo') logoInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, uploadType: FileUploadType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!allowedMimeTypes.includes(file.type)) {
      toast.error(t('fundraiser:giftCertificate.invalidFormat') as string);
      e.target.value = '';
      return;
    }

    try {
      await uploadGiftCertificateFile(fundraiserId, uploadType, file);
      toast.success(t('fundraiser:giftCertificate.uploadSuccess') as string);
      fetchFiles();
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(t('fundraiser:giftCertificate.uploadError') as string);
    } finally {
      e.target.value = '';
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    setCropperOpen(false);
    try {
      await uploadGiftCertificateFile(fundraiserId, 'background', croppedFile);
      toast.success(t('fundraiser:giftCertificate.uploadSuccess') as string);
      fetchFiles();
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(t('fundraiser:giftCertificate.uploadError') as string);
    }
  };

  const handleCropAbort = () => {
    setCropperOpen(false);
  };

  const handleMotifCropComplete = async (croppedFile: File) => {
    setMotifCropperOpen(false);
    try {
      await uploadGiftCertificateFile(fundraiserId, 'motif', croppedFile);
      toast.success(t('fundraiser:giftCertificate.uploadSuccess') as string);
      fetchFiles();
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(t('fundraiser:giftCertificate.uploadError') as string);
    }
  };

  const handleMotifCropAbort = () => {
    setMotifCropperOpen(false);
  };

  const handleSuggestionSelect = async (file: File) => {
    setSuggestionsOpen(false);
    try {
      await uploadGiftCertificateFile(fundraiserId, uploadMode, file);
      toast.success(t('fundraiser:giftCertificate.uploadSuccess') as string);
      fetchFiles();
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(t('fundraiser:giftCertificate.uploadError') as string);
    }
  };

  const handleDeleteClick = (fileName: string) => {
    setFileToDelete(fileName);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = async (result?: { id: string }) => {
    setDeleteDialogOpen(false);
    if (!result) return;

    try {
      await deleteGiftCertificateFile(fundraiserId, result.id);
      toast.success(t('fundraiser:giftCertificate.deleteSuccess') as string);
      fetchFiles();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('fundraiser:giftCertificate.deleteError') as string);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
          {t('fundraiser:giftCertificate.header')}
        </Typography>
        <Divider />
      </Grid>

      <input ref={logoInputRef} type='file' accept='image/png,image/jpeg' hidden onChange={(e) => handleFileChange(e, 'logo')} />

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Vänster: Klickbara wireframe-placeholders */}
          <Grid item xs={12} md={3}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('fundraiser:giftCertificate.templateType')}</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <PlaceholderWireframe
                  mode='background'
                  selected={uploadMode === 'background'}
                  onClick={() => setUploadMode('background')}
                  label={t('fundraiser:giftCertificate.backgroundTemplate')}
                  description={t('fundraiser:giftCertificate.backgroundTemplateDesc')}
                />
              </Grid>
              <Grid item xs={6}>
                <PlaceholderWireframe
                  mode='motif'
                  selected={uploadMode === 'motif'}
                  onClick={() => setUploadMode('motif')}
                  label={t('fundraiser:giftCertificate.motifImage')}
                  description={t('fundraiser:giftCertificate.motifImageDesc')}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Uppladdade filer — visas som wireframes på rad */}
          <Grid item xs>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              {uploadMode === 'background' ? t('fundraiser:giftCertificate.uploadedBackgrounds') : t('fundraiser:giftCertificate.uploadedMotifs')}
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
                p: 2,
              }}
            >
              <Grid container spacing={1.5} alignItems='flex-start'>
                {uploadMode === 'background' && motifs.length > 0 && backgrounds.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity='info'>{t('fundraiser:giftCertificate.backgroundBlockedByMotifs')}</Alert>
                  </Grid>
                )}
                {uploadMode === 'background' &&
                  (motifs.length === 0 || backgrounds.length > 0) &&
                  backgrounds.map((bg) => (
                    <Grid item key={bg.fileName} sx={{ width: 160 }}>
                      <Box>
                        <Box
                          sx={{
                            aspectRatio: '1 / 1.414',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          <Box component='img' src={bg.url} alt={bg.fileName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                        <Button
                          variant='outlined'
                          size='small'
                          color='error'
                          fullWidth
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => handleDeleteClick(bg.fileName)}
                          sx={{ mt: 0.5 }}
                        >
                          {t('fundraiser:giftCertificate.delete')}
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                {uploadMode === 'background' && backgrounds.length === 0 && motifs.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px dashed', borderColor: 'grey.300', borderRadius: 1, p: 3, textAlign: 'center' }}>
                      <FileUploadOutlinedIcon sx={{ fontSize: 32, color: 'grey.400', mb: 0.5 }} />
                      <Typography variant='body2' color='text.secondary'>
                        {t('fundraiser:giftCertificate.noBackgroundsYet')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {uploadMode === 'motif' &&
                  (backgrounds.length === 0 || motifs.length > 0) &&
                  motifs.map((motif) => (
                    <Grid item key={motif.fileName} sx={{ width: 220 }}>
                      <Box>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            p: '2px',
                          }}
                        >
                          <Box
                            component='img'
                            src={motif.url}
                            alt={motif.fileName}
                            sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block', borderRadius: 0.5 }}
                          />
                        </Box>
                        <Button
                          variant='outlined'
                          size='small'
                          color='error'
                          fullWidth
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => handleDeleteClick(motif.fileName)}
                          sx={{ mt: 0.5 }}
                        >
                          {t('fundraiser:giftCertificate.delete')}
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                {uploadMode === 'motif' && backgrounds.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity='info'>{t('fundraiser:giftCertificate.motifBlockedByBackgrounds')}</Alert>
                  </Grid>
                )}
                {uploadMode === 'motif' && backgrounds.length === 0 && motifs.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px dashed', borderColor: 'grey.300', borderRadius: 1, p: 3, textAlign: 'center' }}>
                      <FileUploadOutlinedIcon sx={{ fontSize: 32, color: 'grey.400', mb: 0.5 }} />
                      <Typography variant='body2' color='text.secondary'>
                        {t('fundraiser:giftCertificate.noMotifsYet')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {/* Uppladdningsknapp + förslag */}
                <Grid item xs={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<FileUploadOutlinedIcon />}
                    onClick={() => handleUploadClick(uploadMode)}
                    disabled={
                      loading ||
                      (uploadMode === 'motif' && (motifs.length >= maxMotifs || (backgrounds.length > 0 && motifs.length === 0))) ||
                      (uploadMode === 'background' && motifs.length > 0 && backgrounds.length === 0)
                    }
                  >
                    {uploadMode === 'background' ? t('fundraiser:giftCertificate.uploadBackground') : t('fundraiser:giftCertificate.uploadMotif')}
                  </Button>
                  {uploadMode === 'background' && (
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<AutoAwesomeOutlinedIcon />}
                      onClick={() => setSuggestionsOpen(true)}
                      disabled={loading || (motifs.length > 0 && backgrounds.length === 0)}
                    >
                      {t('fundraiser:giftCertificate.suggestionsButton')}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Kolumn: Logo */}
          <Grid item sx={{ width: 220 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('fundraiser:giftCertificate.logo')}</Typography>
            {logo && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0.5, overflow: 'hidden', p: '6px' }}>
                  <Box
                    component='img'
                    src={logo.url}
                    alt={logo.fileName}
                    sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'contain', display: 'block' }}
                  />
                </Box>
                <Button
                  variant='outlined'
                  size='small'
                  color='error'
                  fullWidth
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleDeleteClick(logo.fileName)}
                  sx={{ mt: 0.5 }}
                >
                  {t('fundraiser:giftCertificate.delete')}
                </Button>
              </Box>
            )}
            <Button
              variant='outlined'
              size='small'
              fullWidth
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => handleUploadClick('logo')}
              disabled={loading}
              sx={{ mt: 0.5 }}
            >
              {t('fundraiser:giftCertificate.uploadLogo')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title={t('fundraiser:giftCertificate.deleteTitle')}
          modaltext={t('fundraiser:giftCertificate.deleteConfirmation')}
          value={fileToDelete}
          onClose={handleDeleteClose}
          confirmButtonColor='error'
        />
      )}

      {cropperOpen && <FundraiserBackgroundCropperModal open={cropperOpen} onComplete={handleCropComplete} onAbort={handleCropAbort} />}

      {suggestionsOpen && (
        <FundraiserSuggestionsModal open={suggestionsOpen} onSelect={handleSuggestionSelect} onClose={() => setSuggestionsOpen(false)} />
      )}

      {motifCropperOpen && (
        <FundraiserMotifCropperModal open={motifCropperOpen} onComplete={handleMotifCropComplete} onAbort={handleMotifCropAbort} />
      )}
    </Grid>
  );
};
